// @greenlight-spec: 20-agent-plugins.md
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { TriangleAlert } from 'lucide-react';

import { Disclosure } from '@/components/greenlight/Disclosure';
import { SectionCard } from '@/components/greenlight/SectionCard';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  buildClaudeBypassSettings,
  buildClaudeManagedSettings,
  formatClaudeSettingsJson,
} from '@/lib/claudeManagedSettings';

import { AdminLink } from './AdminLink';
import { AgentMark, CLAUDE_MARK } from './AgentMark';
import { CodeBlock } from './CodeBlock';

function Tag({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-muted-foreground whitespace-nowrap">
      {children}
    </span>
  );
}

function Strong({ children }: { children: React.ReactNode }): React.ReactElement {
  return <span className="font-medium text-foreground">{children}</span>;
}

/** The Claude rollout in the install wizard's handoff: plugin sync plus the approval policy. */
export function ClaudeManagedSettingsCards(): React.ReactElement {
  const managedJson = formatClaudeSettingsJson(buildClaudeManagedSettings());
  const bypassJson = formatClaudeSettingsJson(buildClaudeBypassSettings());

  return (
    <div className="flex flex-col gap-4" data-testid="claude-managed-settings-cards">
      <SectionCard
        title="Claude — organization rollout"
        icon={<AgentMark {...CLAUDE_MARK} />}
        headerBar
        badge={<Tag>CLAUDE ORG</Tag>}
      >
        <div className="p-5 flex flex-col gap-3 text-sm text-muted-foreground">
          <ol className="list-decimal space-y-2 pl-5 marker:text-foreground">
            <li>
              Open{' '}
              <AdminLink href="https://claude.ai/admin-settings/skills">
                Organization settings → Plugins &amp; skills
              </AdminLink>
              , select <Strong>Add → Sync from GitHub</Strong>, choose your private marketplace
              repository, and set Greenlight&rsquo;s <Strong>Default access</Strong> to{' '}
              <Strong>Installed by default</Strong>. Agent setup has the GitHub App and connector
              steps.
            </li>
            <li>
              Open{' '}
              <AdminLink href="https://claude.ai/admin-settings/claude-code">
                Organization settings → Claude Code
              </AdminLink>
              , select <Strong>Manage</Strong> beside <Strong>Managed settings</Strong>, merge this
              policy and save. If they set <span className="font-mono">disableAutoMode</span>,
              remove it.
            </li>
          </ol>
          <CodeBlock caption="CLAUDE MANAGED SETTINGS" code={managedJson} />
          <Alert variant="warning">
            <TriangleAlert aria-hidden="true" />
            <AlertDescription>
              <p>
                Don&rsquo;t add <span className="font-mono">enabledPlugins</span> or{' '}
                <span className="font-mono">extraKnownMarketplaces</span> for Greenlight here:
                Claude would then skip the organization plugin from step 1, and with a private
                repository nobody gets Greenlight.
              </p>
            </AlertDescription>
          </Alert>
          <Disclosure
            label="Optional: no permission prompts at all"
            hint="bypassPermissions"
            testId="claude-wizard-bypass-option"
          >
            <div className="flex flex-col gap-3 px-4 pb-4 pt-1">
              <p>
                If your organization would rather Claude Code never asked, merge this instead. It
                starts every session with permission prompts off, so every tool call runs at once.
                Leave <span className="font-mono">disableBypassPermissionsMode</span> unset.
              </p>
              <CodeBlock caption="CLAUDE MANAGED SETTINGS · NO PROMPTS" code={bypassJson} />
            </div>
          </Disclosure>
        </div>
      </SectionCard>
    </div>
  );
}
